// V5.26 : Étape Stratégie - "Le Tableau de Commande" REFACTORISÉ
// - Architecture silos étanches avec missionData unifié
// - Sync permanente : chaque clic met à jour le localStorage
// - Retour arrière fidèle : les boutons reflètent l'état exact
// - 9 catégories avec champ "Nom du fournisseur" obligatoire

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Flame, Droplets, Wifi, Shield, Heart, Building2, Dumbbell, Newspaper,
  X, Check, Minus, ArrowRight, ArrowLeft, Sparkles, AlertTriangle, Info,
  FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// V5.26 : Configuration des 9 catégories (SILOS ÉTANCHES)
export const SERVICE_CATEGORIES = [
  {
    id: 'electricite',
    title: 'Électricité',
    description: 'Compteur & Fournisseur',
    icon: Zap,
    color: 'amber',
    glowColor: 'rgba(251, 191, 36, 0.3)',
    activateLabel: 'Activer pour ma nouvelle adresse',
    deactivateLabel: 'Résilier mon contrat',
    providerPlaceholder: 'Ex: EDF, Engie, TotalEnergies...',
    requiresProvider: true
  },
  {
    id: 'gaz',
    title: 'Gaz',
    description: 'Compteur & Fournisseur',
    icon: Flame,
    color: 'orange',
    glowColor: 'rgba(249, 115, 22, 0.3)',
    activateLabel: 'Activer pour ma nouvelle adresse',
    deactivateLabel: 'Résilier mon contrat',
    providerPlaceholder: 'Ex: GRDF, Engie, Eni...',
    requiresProvider: true
  },
  {
    id: 'eau',
    title: 'Eau',
    description: 'Distribution & Assainissement',
    icon: Droplets,
    color: 'sky',
    glowColor: 'rgba(56, 189, 248, 0.3)',
    activateLabel: 'Transférer mon abonnement',
    deactivateLabel: 'Résilier mon contrat',
    providerPlaceholder: 'Ex: Veolia, Suez, Lyonnaise...',
    requiresProvider: true
  },
  {
    id: 'internet',
    title: 'Box Internet & Fibre',
    description: 'Connexion Internet fixe',
    icon: Wifi,
    color: 'blue',
    glowColor: 'rgba(59, 130, 246, 0.3)',
    activateLabel: 'Déménager ma ligne',
    deactivateLabel: 'Résilier ma box',
    providerPlaceholder: 'Ex: Orange, Free, SFR, Bouygues...',
    requiresProvider: true
  },
  {
    id: 'assurance',
    title: 'Assurance Habitation',
    description: 'Multirisque habitation',
    icon: Shield,
    color: 'purple',
    glowColor: 'rgba(168, 85, 247, 0.3)',
    activateLabel: 'Mettre à jour mon adresse',
    deactivateLabel: 'Résilier mon contrat',
    providerPlaceholder: 'Ex: MAIF, Macif, AXA, Allianz...',
    requiresProvider: true
  },
  {
    id: 'mutuelle',
    title: 'Mutuelle Santé',
    description: 'Complémentaire santé',
    icon: Heart,
    color: 'rose',
    glowColor: 'rgba(244, 63, 94, 0.3)',
    activateLabel: 'Mettre à jour mes coordonnées',
    deactivateLabel: 'Résilier ma mutuelle',
    providerPlaceholder: 'Ex: Harmonie, MGEN, Alan...',
    requiresProvider: true
  },
  {
    id: 'banque',
    title: 'Banque & Finance',
    description: 'Notification de changement d\'adresse',
    icon: Building2,
    color: 'cyan',
    glowColor: 'rgba(34, 211, 238, 0.3)',
    activateLabel: 'Notifier mon changement d\'adresse',
    deactivateLabel: null,
    deactivateBlocked: true,
    deactivateBlockedMessage: "La clôture de compte n'est pas gérée dans ce pack. Choisissez 'Je ne touche pas'.",
    providerPlaceholder: 'Ex: BNP Paribas, Crédit Agricole...',
    requiresProvider: true,
    legalBasis: 'Article 16 RGPD (Droit de rectification)'
  },
  {
    id: 'sport',
    title: 'Salles de Sport / Fitness',
    description: 'Abonnement salle',
    icon: Dumbbell,
    color: 'emerald',
    glowColor: 'rgba(16, 185, 129, 0.3)',
    activateLabel: 'Transférer dans une autre salle',
    deactivateLabel: 'Résilier mon abonnement',
    providerPlaceholder: 'Ex: Basic Fit, Fitness Park...',
    requiresProvider: true
  },
  {
    id: 'presse',
    title: 'Presse & Magazines',
    description: 'Abonnements papier',
    icon: Newspaper,
    color: 'slate',
    glowColor: 'rgba(148, 163, 184, 0.3)',
    activateLabel: 'Mettre à jour mon adresse de livraison',
    deactivateLabel: 'Résilier mes abonnements',
    providerPlaceholder: 'Ex: Le Monde, L\'Équipe...',
    requiresProvider: true
  }
];

// V1.2 : Catégories spécifiques Mission Héritage / Succession.
// Adaptées à la logique post-décès : pas de salle de sport, ajout des
// assurances vie / prévoyance qui sont centrales pour une succession.
export const SUCCESSION_CATEGORIES = [
  {
    id: 'banque',
    title: 'Banques & Finance',
    description: 'Notification & clôture',
    icon: Building2,
    color: 'cyan',
    glowColor: 'rgba(34, 211, 238, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Notifier le décès & clôturer',
    providerPlaceholder: 'Ex: BNP, Crédit Agricole, LCL, Société Générale...',
    requiresProvider: true,
    legalBasis: 'Article 16 RGPD + Code civil (succession)'
  },
  {
    id: 'assurance_vie',
    title: 'Assurance Vie & Prévoyance',
    description: 'Contrats du défunt',
    icon: Shield,
    color: 'purple',
    glowColor: 'rgba(168, 85, 247, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Notifier le décès & enclencher',
    providerPlaceholder: 'Ex: AXA, Allianz, Generali, CNP...',
    requiresProvider: true
  },
  {
    id: 'assurance_habitation',
    title: 'Assurance Habitation',
    description: 'Multirisque habitation (MRH) du défunt',
    icon: Shield,
    color: 'indigo',
    glowColor: 'rgba(99, 102, 241, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier la MRH',
    providerPlaceholder: 'Ex: MAIF, Macif, Matmut, GMF, Groupama...',
    requiresProvider: true
  },
  {
    id: 'assurance_auto',
    title: 'Assurance Auto',
    description: 'Contrat auto / 2 roues du défunt',
    icon: Shield,
    color: 'cyan',
    glowColor: 'rgba(6, 182, 212, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier l\'assurance auto',
    providerPlaceholder: 'Ex: Direct Assurance, AXA, MAIF, GMF...',
    requiresProvider: true
  },
  {
    id: 'mutuelle',
    title: 'Mutuelle Santé',
    description: 'Complémentaire santé',
    icon: Heart,
    color: 'rose',
    glowColor: 'rgba(244, 63, 94, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier la mutuelle',
    providerPlaceholder: 'Ex: Harmonie, MGEN, Alan, Malakoff Humanis...',
    requiresProvider: true
  },
  {
    id: 'energie',
    title: 'Énergie (Électricité & Gaz)',
    description: 'EDF, Engie, TotalEnergies...',
    icon: Zap,
    color: 'amber',
    glowColor: 'rgba(251, 191, 36, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier le contrat',
    providerPlaceholder: 'Ex: EDF, Engie, TotalEnergies...',
    requiresProvider: true
  },
  {
    id: 'eau',
    title: 'Eau',
    description: 'Distribution & assainissement',
    icon: Droplets,
    color: 'sky',
    glowColor: 'rgba(56, 189, 248, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier l\'abonnement',
    providerPlaceholder: 'Ex: Veolia, Suez, Lyonnaise des Eaux...',
    requiresProvider: true
  },
  {
    id: 'telecom',
    title: 'Internet & Télécom',
    description: 'Box, mobile & fixe',
    icon: Wifi,
    color: 'blue',
    glowColor: 'rgba(59, 130, 246, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier les lignes',
    providerPlaceholder: 'Ex: Orange, Free, SFR, Bouygues...',
    requiresProvider: true
  },
  {
    id: 'sport',
    title: 'Salles de Sport / Fitness',
    description: 'Abonnement en cours',
    icon: Dumbbell,
    color: 'emerald',
    glowColor: 'rgba(16, 185, 129, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier par LRAR',
    providerPlaceholder: 'Ex: Basic Fit, Fitness Park, Keep Cool...',
    requiresProvider: true
  },
  {
    id: 'presse',
    title: 'Presse & Magazines',
    description: 'Abonnements papier',
    icon: Newspaper,
    color: 'slate',
    glowColor: 'rgba(148, 163, 184, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier les abonnements',
    providerPlaceholder: 'Ex: Le Monde, L\'Équipe, Paris Match...',
    requiresProvider: true
  },
  // V1.2 / DDD.6 / DDD.9 : Démarches administratives Succession.
  // Ces entrées sont des SIGNALEMENTS de décès par courrier (LRAR + acte décès) —
  // PAS des résiliations classiques. Le wording "Je signale" est utilisé via `actionType: 'declaration'`.
  // DDD.9 : Véhicule ANTS RETIRÉ — la déclaration de cession au SIV requiert FranceConnect
  // du défunt OU une intervention du notaire. Pas automatisable par mandat juridique seul.
  // L'assurance auto reste résiliable via la catégorie "Assurance Auto" plus haut.
  {
    id: 'bail',
    title: 'Bail de location',
    description: 'Résiliation du bail du défunt par LRAR (loi Mermaz)',
    icon: FileText,
    color: 'blue',
    glowColor: 'rgba(59, 130, 246, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Résilier le bail (LRAR)',
    providerPlaceholder: 'Nom du bailleur ou de l\'agence',
    requiresProvider: true,
    actionType: 'resiliation',
    legalBasis: 'Article 14 loi 89-462 (loi Mermaz) — Résiliation sans préavis en cas de décès'
  },
  {
    id: 'impots',
    title: 'Impôts (DGFiP)',
    description: 'Signalement du décès au Centre des Finances Publiques du domicile du défunt',
    icon: Building2,
    color: 'red',
    glowColor: 'rgba(239, 68, 68, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Signaler à la DGFiP',
    providerPlaceholder: 'Ville du défunt (ex: Paris, Bordeaux...)',
    requiresProvider: true,
    actionType: 'declaration',
    isAdministrative: true,
    legalBasis: 'Notification simple par LRAR avec acte de décès. La déclaration de succession formelle reste à la charge du notaire (art. 641 CGI, sous 6 mois).'
  },
  {
    id: 'caf',
    title: 'CAF',
    description: 'Signalement du décès à la Caisse d\'Allocations Familiales',
    icon: Building2,
    color: 'red',
    glowColor: 'rgba(239, 68, 68, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Signaler à la CAF',
    providerPlaceholder: 'Département ou ville du défunt (ex: 33 Bordeaux)',
    requiresProvider: true,
    actionType: 'declaration',
    isAdministrative: true,
    legalBasis: 'Arrêt automatique des prestations sous 30 jours après notification (LRAR + acte décès)'
  },
  {
    id: 'cpam',
    title: 'CPAM / Sécurité sociale',
    description: 'Signalement du décès et déclenchement éventuel du capital décès',
    icon: Heart,
    color: 'red',
    glowColor: 'rgba(239, 68, 68, 0.3)',
    activateLabel: null,
    deactivateLabel: 'Signaler à la CPAM',
    providerPlaceholder: 'Département ou ville du défunt (ex: 75 Paris)',
    requiresProvider: true,
    actionType: 'declaration',
    isAdministrative: true,
    legalBasis: 'Capital décès aux ayants droit (art. L361-1 CSS) — courrier LRAR avec acte de décès et RIB ayant droit'
  }
];

// V5.26 : Composant Carte Catégorie avec Sync Permanente
const CategoryCard = ({
  category,
  status,
  providerName,
  onStatusChange,
  onProviderChange,
  showBlockedMessage,
  onShowBlockedMessage,
  mode = 'demenagement',  // V1.2 : 'demenagement' | 'succession' — en succession on cache "Je garde"
}) => {
  const Icon = category.icon;
  const isActivate = status === 'active';
  const isDeactivate = status === 'desactive';
  const isNeutral = status === 'none' || !status;
  const isSelected = isActivate || isDeactivate;
  
  // V5.26 : Styles dynamiques avec glow pour chaque état
  const getCardStyle = () => {
    if (isActivate) return {
      border: '1px solid rgba(16, 185, 129, 0.6)',
      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(0,0,0,0) 100%)',
      boxShadow: '0 0 25px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
    };
    if (isDeactivate) return {
      border: '1px solid rgba(239, 68, 68, 0.6)',
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(0,0,0,0) 100%)',
      boxShadow: '0 0 25px rgba(239, 68, 68, 0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
    };
    return {
      border: '1px solid rgba(255,255,255,0.1)',
      background: 'rgba(255,255,255,0.02)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
    };
  };
  
  const handleDeactivateClick = () => {
    if (category.deactivateBlocked) {
      onShowBlockedMessage?.(category.id);
      return;
    }
    onStatusChange('desactive');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ 
        y: -4, 
        boxShadow: `0 8px 30px ${category.glowColor}`,
        transition: { duration: 0.2 }
      }}
      style={getCardStyle()}
      className="relative rounded-xl p-5 transition-all duration-300 backdrop-blur-sm"
      data-testid={`category-card-${category.id}`}
    >
      {/* Badge de sélection avec animation */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className={`absolute -top-2 -right-2 px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
              isActivate
                ? 'bg-emerald-500 text-white shadow-emerald-500/30'
                : 'bg-red-500 text-white shadow-red-500/30'
            }`}
          >
            {isActivate ? "Je garde" : (category.actionType === 'declaration' ? "Je signale" : "Je résilie")}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Header avec icône */}
      <div className="flex items-start gap-4 mb-4">
        <motion.div 
          whileHover={{ scale: 1.1 }}
          className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${category.color}-500/20 to-${category.color}-600/10 
            flex items-center justify-center border border-${category.color}-500/30`}
          style={{ boxShadow: `0 0 15px ${category.glowColor}` }}
        >
          <Icon className={`w-6 h-6 text-${category.color}-400`} />
        </motion.div>
        <div>
          <h3 className="font-semibold text-white text-lg">{category.title}</h3>
          <p className="text-sm text-gray-500">{category.description}</p>
        </div>
      </div>
      
      {/* V5.26 : Champ Fournisseur - affiché seulement si sélectionné */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4"
          >
            <label className="block text-xs text-gray-400 mb-1">
              Nom du fournisseur / organisme <span className="text-red-400">*</span>
            </label>
            <Input
              value={providerName || ''}
              onChange={(e) => onProviderChange(e.target.value)}
              placeholder={category.providerPlaceholder}
              className="bg-gray-800/50 border-gray-700 text-white text-sm h-9"
              autoComplete="off"
              data-testid={`${category.id}-provider-input`}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* V1.2 : grille 3 colonnes pour déménagement (Je garde / Je résilie / Je ne touche pas)
          ou 2 colonnes pour succession (Je résilie / Je ne touche pas uniquement) */}
      <div className={`grid ${mode === 'succession' ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
        {/* Bouton "Je garde" : masqué en mode succession (un défunt ne garde rien) */}
        {mode !== 'succession' && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onStatusChange('active')}
          className={`flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg text-xs font-medium transition-all ${
            isActivate
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 shadow-lg shadow-emerald-500/10'
              : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:border-emerald-500/30 hover:text-emerald-400'
          }`}
          data-testid={`${category.id}-activate-btn`}
        >
          <Check className="w-5 h-5" />
          <span className="text-center leading-tight">Je garde<br/><span className="text-[10px] opacity-70">(transfert)</span></span>
        </motion.button>
        )}

        {/* Bouton Je désactive [✗] */}
        <motion.button
          whileHover={{ scale: category.deactivateBlocked ? 1 : 1.02 }}
          whileTap={{ scale: category.deactivateBlocked ? 1 : 0.98 }}
          onClick={handleDeactivateClick}
          className={`flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg text-xs font-medium transition-all ${
            category.deactivateBlocked
              ? 'bg-gray-800/30 text-gray-600 border border-gray-700/30 cursor-not-allowed'
              : isDeactivate
                ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-lg shadow-red-500/10'
                : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:border-red-500/30 hover:text-red-400'
          }`}
          data-testid={`${category.id}-deactivate-btn`}
          disabled={category.deactivateBlocked}
        >
          <X className="w-5 h-5" />
          <span className="text-center leading-tight">
            {category.actionType === 'declaration' ? 'Je signale' : 'Je résilie'}
            <br/>
            <span className="text-[10px] opacity-70">
              {category.actionType === 'declaration' ? '(notification décès)' : '(fin contrat)'}
            </span>
          </span>
        </motion.button>
        
        {/* Bouton Je ne touche pas [-] */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onStatusChange('none')}
          className={`flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg text-xs font-medium transition-all ${
            isNeutral
              ? 'bg-white/10 text-white border border-white/30 shadow-lg shadow-white/5'
              : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:border-white/20 hover:text-gray-300'
          }`}
          data-testid={`${category.id}-neutral-btn`}
        >
          <Minus className="w-5 h-5" />
          <span className="text-center leading-tight">Je ne touche pas</span>
        </motion.button>
      </div>
      
      {/* Message d'information pour Banque */}
      <AnimatePresence>
        {showBlockedMessage === category.id && category.deactivateBlocked && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-400 text-xs">
                {category.deactivateBlockedMessage}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Note légale pour Banque */}
      {category.legalBasis && isActivate && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-3 pt-3 border-t border-gray-700/50"
        >
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Info className="w-3 h-3" />
            <span>{category.legalBasis}</span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// V5.26 : Composant principal avec architecture missionData
// V1.2 : ajout de la prop `mode` ('demenagement' | 'succession')
const StrategyStepV526 = ({
  missionData,
  onMissionDataChange,
  onNext,
  onBack,
  mode = 'demenagement',
}) => {
  const [showBlockedMessage, setShowBlockedMessage] = useState(null);
  
  // V5.26 : Extraire les données des catégories depuis missionData
  const categories = missionData?.categories || {};
  
  // V5.26 : Handler de changement de status - Sync permanente
  const handleStatusChange = useCallback((categoryId, newStatus) => {
    const updatedCategories = {
      ...categories,
      [categoryId]: {
        ...categories[categoryId],
        status: newStatus,
        // Effacer le provider si on passe en 'none'
        provider: newStatus === 'none' ? '' : categories[categoryId]?.provider || ''
      }
    };
    
    // Appeler le callback parent pour mettre à jour missionData
    onMissionDataChange({
      ...missionData,
      categories: updatedCategories
    });
    
    // Fermer le message bloqué si on change de choix
    if (showBlockedMessage === categoryId) {
      setShowBlockedMessage(null);
    }
  }, [categories, missionData, onMissionDataChange, showBlockedMessage]);
  
  // V5.26 : Handler de changement de provider - Sync permanente
  const handleProviderChange = useCallback((categoryId, provider) => {
    const updatedCategories = {
      ...categories,
      [categoryId]: {
        ...categories[categoryId],
        provider: provider
      }
    };
    
    onMissionDataChange({
      ...missionData,
      categories: updatedCategories
    });
  }, [categories, missionData, onMissionDataChange]);
  
  // Compteurs
  const activateCount = Object.values(categories).filter(c => c?.status === 'active').length;
  const deactivateCount = Object.values(categories).filter(c => c?.status === 'desactive').length;
  const totalSelected = activateCount + deactivateCount;
  
  // V5.26 : Vérifier que tous les fournisseurs sont remplis
  const getMissingProviders = () => {
    const missing = [];
    // V1.2 : on cherche la catégorie dans la bonne liste selon le mode
    const activeList = mode === 'succession' ? SUCCESSION_CATEGORIES : SERVICE_CATEGORIES;
    Object.entries(categories).forEach(([catId, catData]) => {
      if ((catData?.status === 'active' || catData?.status === 'desactive') && !catData?.provider) {
        const cat = activeList.find(c => c.id === catId);
        if (cat) missing.push(cat.title);
      }
    });
    return missing;
  };
  
  // V5.26 : Continuer vers l'étape 3
  const handleContinue = () => {
    const missingProviders = getMissingProviders();
    if (missingProviders.length > 0) {
      alert(`Veuillez renseigner le nom du fournisseur pour : ${missingProviders.join(', ')}`);
      return;
    }
    
    if (totalSelected === 0) {
      alert('Veuillez sélectionner au moins une catégorie');
      return;
    }
    
    onNext?.();
  };
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-4">
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <span className="text-cyan-400 text-sm font-medium">Étape 2 sur 4</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">
          {mode === 'succession'
            ? 'Gérez les contrats du défunt'
            : 'Prenez le contrôle de votre déménagement'}
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
          {mode === 'succession' ? (
            <>
              Pour chaque fournisseur listé ci-dessous, choisissez de{' '}
              <span className="text-red-400 font-medium">résilier</span> le contrat du défunt
              ou de ne pas y toucher (par exemple s'il est géré par le notaire).
              Toutes les démarches seront effectuées sous mandat juridique.
            </>
          ) : (
            <>
              Pour chaque fournisseur listé ci-dessous, choisissez ce que vous souhaitez :
              le <span className="text-emerald-400 font-medium">garder</span> en transférant le contrat vers votre nouvelle adresse,
              ou le <span className="text-red-400 font-medium">résilier</span> définitivement.
              Si vous préférez gérer un fournisseur seul, choisissez '<span className="text-white font-medium">Je ne touche pas</span>'.
            </>
          )}
        </p>
      </motion.div>
      
      {/* Indicateurs */}
      <AnimatePresence>
        {totalSelected > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex justify-center gap-4 flex-wrap"
          >
            {mode !== 'succession' && activateCount > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30"
              >
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">
                  {activateCount} à garder (transfert)
                </span>
              </motion.div>
            )}
            {deactivateCount > 0 && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-500/30"
              >
                <X className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm font-medium">
                  {deactivateCount} à résilier
                </span>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* V1.2 : choix de la liste de catégories selon le mode */}
      {(() => { const categoriesList = mode === 'succession' ? SUCCESSION_CATEGORIES : SERVICE_CATEGORIES; return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categoriesList.map((category, idx) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <CategoryCard
              category={category}
              status={categories[category.id]?.status || 'none'}
              providerName={categories[category.id]?.provider || ''}
              onStatusChange={(status) => handleStatusChange(category.id, status)}
              onProviderChange={(provider) => handleProviderChange(category.id, provider)}
              showBlockedMessage={showBlockedMessage}
              onShowBlockedMessage={setShowBlockedMessage}
              mode={mode}
            />
          </motion.div>
        ))}
      </div>
      ); })()}

      {/* Navigation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex justify-between items-center pt-6 border-t border-gray-800"
      >
        <Button
          variant="outline"
          onClick={onBack}
          className="border-gray-700 text-gray-400 hover:text-white"
          data-testid="strategy-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        
        <Button
          onClick={handleContinue}
          disabled={totalSelected === 0}
          className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="strategy-continue-btn"
        >
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </motion.div>
    </div>
  );
};

export default StrategyStepV526;
